<div class="max-w-7xl mx-auto px-4 my-8">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
        <app-btn-back routeTo=".."></app-btn-back>
    </div>

    <div class="flex items-start mb-4">
        <h1 class="text-2xl font-bold text-primary-500">Billing & Subscriptions</h1>
    </div>

    <div class="flex flex-col gap-4 w-full">

        <div class="flex flex-row gap-4 w-full">
            <!-- Plan Status Card (Compact) -->
            <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 relative overflow-hidden w-full">
                <div class="absolute right-0 top-0 w-24 h-24 bg-primary-50 rounded-full filter blur-xl -mr-12 -mt-12 pointer-events-none">
                </div>

                <div class="flex items-center justify-between gap-4 mb-3 relative z-10">
                    <h2 class="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                        <i class="fa fa-info-circle text-primary-500"></i> Plan Status
                    </h2>

                    @if (getSeatsUsed() > freeSeats) {
                    <div class="flex gap-2 shrink-0">
                        <button
                            [class]="isGroupSubscribedToSeats() ? 'bg-primary-500 hover:bg-primary-600 text-white font-bold px-4 py-2 rounded-lg transition shadow-sm text-xs' : 'bg-red-500 hover:bg-red-600 text-white font-bold px-4 py-2 rounded-lg transition shadow-sm text-xs'"
                            (click)="onChangePlan()">
                            Subscribe Now
                        </button>
                    </div>
                    }
                </div>

                <div>
                    @if (isGroupSubscribedToSeats()) {
                    @let details = getSeatsSubscriptionDetails();
                    <div class="flex flex-wrap items-center gap-2 mb-1.5">
                        <span
                            class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xxs font-semibold bg-primary-50 text-primary-700">
                            <span class="h-1.5 w-1.5 rounded-full bg-primary-500"></span> Active Paid Plan
                        </span>
                    </div>
                    <p class="text-xs text-gray-500">
                        Paid by <strong class="text-gray-700">{{ details?.payerName }}</strong> &bull;
                        <span class="capitalize font-semibold text-gray-700">{{ details?.interval }}ly</span>
                        &bull;
                        Next billing: <span class="font-semibold text-gray-700">{{ details?.currentPeriodEnd |
                            date:'mediumDate' }}</span>
                    </p>
                    } @else {
                    <div class="flex flex-wrap items-center gap-2 mb-1.5">
                        <span
                            class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xxs font-semibold bg-gray-100 text-gray-700">
                            <span class="h-1.5 w-1.5 rounded-full bg-gray-400"></span> Free Tier
                        </span>
                    </div>
                    <p class="text-xs text-gray-500 leading-relaxed">
                        Your {{ group?.groupType === 'church' ? 'church' : 'school' }} group is currently on the free tier. Subscription is required once active
                        {{ group?.groupType === 'church' ? 'members' : 'students' }} headcount exceeds {{freeSeats}} seats.
                    </p>
                    @if (getSeatsUsed() > freeSeats) {
                    <div
                        class="mt-2.5 p-2.5 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-start gap-2">
                        <i class="fa fa-exclamation-triangle mt-0.5 shrink-0 text-red-500"></i>
                        <div class="text-xxs leading-normal">
                            <strong>Subscription Required:</strong> Active {{ group?.groupType === 'church' ? 'member' : 'student' }} headcount is {{
                            getSeatsUsed() }}, which exceeds the limit of {{ freeSeats }} free seats.
                        </div>
                    </div>
                    }
                    }
                    @if (getNextBillingDetails(); as nextBill) {
                    <div class="mt-3 pt-3 border-t border-gray-100 flex flex-col gap-1">
                        <span class="text-xxs font-bold text-gray-400 uppercase tracking-wider">Upcoming Billing
                            (Next 30 Days)</span>
                        <p class="text-xs text-gray-500">
                            Next billing date: <strong class="text-gray-700">{{ nextBill.date | date:'mediumDate'
                                }}</strong>
                        </p>
                        <p class="text-xs text-gray-500">
                            Products: <strong class="text-gray-700">{{ nextBill.products.join(', ') }}</strong>
                        </p>
                    </div>
                    }
                </div>
            </div>

            <!-- Small Metric Cards in a Column -->
            <div class="flex flex-col gap-4 w-full">
                <!-- Seats Metric -->
                <div
                    class="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 hover:shadow-md transition duration-300 w-full">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center gap-2.5">
                            <span
                                class="w-10 h-10 min-w-10 min-h-10 items-center flex justify-center rounded-lg bg-blue-50 text-blue-500">
                                <i class="fa fa-users text-sm"></i>
                            </span>
                            <div>
                                <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ group?.groupType === 'church' ? 'Member' : 'Student' }} Seats
                                </h3>
                                <p class="text-sm font-black text-gray-900 mt-0.5">
                                    {{ getSeatsUsed() }} <span class="text-xs font-normal text-gray-400">/ {{
                                        getSeatsLimit() }} used</span>
                                </p>
                            </div>
                        </div>
                        <span [class]="getSeatsUsagePercent() > 90 ? 'text-red-500 font-bold' : 'text-gray-400'"
                            class="text-xs font-semibold">
                            {{ getSeatsUsagePercent() | number:'1.0-0' }}%
                        </span>
                    </div>
                    <div class="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden mb-1">
                        <div class="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                            [style.width.%]="getSeatsUsagePercent()"></div>
                    </div>
                    <div class="flex justify-between text-xxs text-gray-400 font-medium">
                        <span>Active {{ group?.groupType === 'church' ? 'members' : 'students' }} in group</span>
                        <span>{{ getFreeSeats() }} seats available</span>
                    </div>
                </div>

                <!-- Storage Metric -->
                <div
                    class="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 hover:shadow-md transition duration-300 w-full">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center gap-2.5">
                            <span
                                class="w-10 h-10 min-w-10 min-h-10 items-center flex justify-center rounded-lg bg-indigo-50 text-indigo-500">
                                <i class="fa fa-cloud text-sm"></i>
                            </span>
                            <div>
                                <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider">Cloud Storage
                                </h3>
                                <p class="text-sm font-black text-gray-900 mt-0.5">
                                    {{ formatBytes(getStorageUsageBytes()) }} <span
                                        class="text-xs font-normal text-gray-400">/
                                        {{ getStorageLimitGb() }}
                                        GB</span>
                                </p>
                            </div>
                        </div>
                        <span [class]="getStorageUsagePercent() > 90 ? 'text-red-500 font-bold' : 'text-gray-400'"
                            class="text-xs font-semibold">
                            {{ getStorageUsagePercent() | number:'1.0-0' }}%
                        </span>
                    </div>
                    <div class="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden mb-1">
                        <div class="bg-indigo-500 h-1.5 rounded-full transition-all duration-500"
                            [style.width.%]="getStorageUsagePercent()"></div>
                    </div>
                    <div class="flex justify-between text-xxs text-gray-400 font-medium">
                        <span>Subscribed files & media storage</span>
                        <span>{{ formatBytes(getFreeStorageBytes()) }} free</span>
                    </div>
                </div>

                <!-- AI Credits Metric -->
                <div [routerLink]="['transactions']"
                    class="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 hover:shadow-md hover:border-purple-300 cursor-pointer transition duration-300 w-full group flex items-center justify-between">
                    <div class="flex items-center gap-3.5">
                        <span
                            class="w-11 h-11 min-w-11 min-h-11 items-center flex justify-center rounded-xl bg-purple-50 text-purple-500 group-hover:bg-purple-100 transition">
                            <i class="fa fa-magic text-base"></i>
                        </span>
                        <div>
                            <h3
                                class="text-xs font-bold text-gray-400 uppercase tracking-wider group-hover:text-purple-600 transition flex items-center gap-1.5">
                                AI Credits
                                <i class="fa fa-chevron-right text-xxs text-gray-300 group-hover:text-purple-500"></i>
                            </h3>
                            <p class="text-2xl font-black text-gray-900 mt-0.5">
                                {{ getAiCreditsActive() | number }}
                            </p>
                        </div>
                    </div>
                    <span class="text-xs font-semibold text-purple-600 group-hover:underline flex items-center gap-1">
                        Transactions &rarr;
                    </span>
                </div>
            </div>
        </div>

        <!-- Active Add-ons Card -->
        <div class="w-full shrink-0 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
            <div class="flex justify-between items-start mb-6">
                <div>
                    <h2 class="text-lg font-bold text-gray-900">Active Add-Ons</h2>
                    <p class="text-xs text-gray-500 mt-0.5">Purchased add-on products currently linked to this
                        group.</p>
                </div>
                <app-btn-action label="Add Product" icon="fa fa-plus" (clicked)="onSubscribe()"
                    bgColor="bg-gray-100 hover:bg-gray-200 active:bg-gray-200" [shadow]="false" padding="px-2"
                    textColor="text-primary-500"></app-btn-action>
            </div>

            <div class="flex flex-col gap-4">
                @for (prod of getSubscribedProducts(); track prod.itemId) {
                <div
                    class="flex flex-col p-4 rounded-xl border border-gray-100 hover:border-gray-200 transition bg-gray-50/30 gap-2.5">
                    <!-- Top Row: Name, interval, qty (left) and Price (right) -->
                    <div class="flex flex-row justify-between items-center w-full">
                        <div class="flex items-center gap-2">
                            <span class="font-bold text-gray-900">{{ prod.productName }}</span>
                            <app-chip label="{{ prod.interval }}ly" [color]="chipColor.SUCCESS"
                                padding="px-1.5 py-0.5"></app-chip>
                            @if (prod.quantity > 1) {
                            <app-chip label="Qty: {{ prod.quantity }}" [color]="chipColor.INFO"
                                padding="px-1.5 py-0.5"></app-chip>
                            }
                        </div>

                        <span class="font-bold text-gray-900 shrink-0">
                            @if (prod.amount !== null) {
                            {{ formatPrice(prod.amount, prod.currency) }}<span
                                class="text-xs font-normal text-gray-500">/{{ prod.interval }}</span>
                            } @else {
                            Metered Usage
                            }
                        </span>
                    </div>

                    <!-- Middle: Description -->
                    <p class="text-xs text-gray-500 leading-normal">{{ prod.productDescription }}</p>

                    <!-- Bottom Row: Payer (left) and Delete Action (right) -->
                    <div class="flex flex-row justify-between items-center w-full border-t border-gray-100 pt-2.5 mt-1">
                        <div class="flex items-center gap-1.5 text-xs text-gray-400">
                            <i class="fa fa-credit-card text-gray-400"></i>
                            <span>Payer: <strong class="text-gray-600">{{ prod.payerName }}</strong></span>
                        </div>

                        <div>
                            @if (prod.payerId === user?.uid) {
                            <app-btn-fab (click)="onDeleteProduct(prod.subscriptionId, prod.itemId)"
                                icon="fa fa-trash-can" size="xs"
                                bgColor="bg-transparent hover:bg-red-500 active:bg-red-500"
                                textColor="text-red-600 hover:text-white active:text-white" [position]="position.LEFT"
                                tooltip="Unsubscribe">
                            </app-btn-fab>
                            } @else {
                            <span class="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded"
                                title="Only the card owner can unsubscribe this item">
                                Locked
                            </span>
                            }
                        </div>
                    </div>
                </div>
                } @empty {
                <div class="text-center py-8 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                    <i class="fa fa-cubes text-gray-300 text-3xl mb-2"></i>
                    <p class="text-sm font-medium text-gray-500">No Active Add-On Products</p>
                    <p class="text-xs text-gray-400 mt-0.5">Click "Add Product" above to view available storage and
                        AI tokens packages.</p>
                </div>
                }
            </div>
        </div>

        <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col gap-4">
            <div>
                <h2 class="text-lg font-bold text-gray-900">Payment History</h2>
                <p class="text-xs text-gray-500 mt-0.5">View and download your monthly invoices.</p>
            </div>

            <div class="flex flex-col gap-2 max-h-[400px] overflow-y-auto pr-1">
                @for (bill of bills; track bill.id) {
                <div [class]="(selectedInvoice?.id === bill.id ? 'border-primary-500 bg-primary-50/10' : 'border-gray-100 bg-white') + ' flex flex-col p-3.5 rounded-xl border hover:border-gray-200 cursor-pointer transition select-none'"
                    (click)="selectBill(bill)">

                    <div class="flex justify-between items-start mb-2">
                        <div class="flex flex-col">
                            <span class="font-bold text-sm text-gray-900">{{ bill.number || 'Invoice' }}</span>
                            <span class="text-xxs text-gray-400 mt-0.5">{{ bill.created | date:'mediumDate'
                                }}</span>
                        </div>
                        <span
                            [class]="bill.status === 'paid' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'"
                            class="text-xxs px-2 py-0.5 rounded-full font-semibold border capitalize">
                            {{ bill.status }}
                        </span>
                    </div>

                    <div class="flex justify-between items-center pt-2 border-t border-gray-50">
                        <span class="font-extrabold text-sm text-gray-900">{{ bill.amountPaid | currency:
                            bill.currency }}</span>

                        <div class="flex items-center gap-2">
                            @if (bill.pdf) {
                            <a [href]="bill.pdf" target="_blank" (click)="$event.stopPropagation()"
                                class="text-gray-400 hover:text-red-500 p-1 rounded-lg transition" title="Download PDF">
                                <i class="fa fa-file-pdf-o text-base"></i>
                            </a>
                            }
                            <i class="fa" [class.fa-chevron-down]="selectedInvoice?.id !== bill.id"
                                [class.fa-chevron-up]="selectedInvoice?.id === bill.id"
                                class="text-xs text-gray-400"></i>
                        </div>
                    </div>
                </div>
                } @empty {
                <div class="text-center py-8 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                    <i class="fa fa-file-text-o text-gray-300 text-3xl mb-2"></i>
                    <p class="text-xs font-medium text-gray-400">No payment history found</p>
                </div>
                }
            </div>
        </div>

    </div>

    <!-- Usage Details and Break-up (Reveals when invoice selected) -->
    @if (selectedInvoice) {
    <div class="mt-8 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <div class="flex justify-between items-center border-b border-gray-100 pb-4 mb-6">
            <div>
                <h3 class="text-xl font-bold text-gray-900">Billing Detail & Usage Break-up</h3>
                <p class="text-xs text-gray-500 mt-1">Invoice: <strong>{{ selectedInvoice.number }}</strong> ({{
                    selectedInvoice.created | date:'MMMM YYYY' }})</p>
            </div>
            <button class="text-gray-400 hover:text-gray-600 font-semibold text-sm flex items-center gap-1"
                (click)="selectedInvoice = null">
                Close Details <i class="fa fa-times"></i>
            </button>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <!-- Left panel: Invoice line items details -->
            <div class="lg:col-span-5 bg-gray-50/50 rounded-xl p-5 border border-gray-100 flex flex-col gap-4">
                <h4 class="font-bold text-sm text-gray-700 mb-2">Invoice Summary</h4>
                <div class="flex flex-col gap-3">
                    @for (line of selectedInvoice.lines; track $index) {
                    <div class="flex justify-between items-start py-2 border-b border-gray-100 last:border-0">
                        <div class="flex flex-col max-w-[70%]">
                            <span class="font-semibold text-xs text-gray-800 leading-snug">{{ line.description }}</span>
                            <span class="text-xxs text-gray-400 mt-0.5">Period: {{ line.periodStart | date:'shortDate'
                                }} - {{ line.periodEnd | date:'shortDate' }}</span>
                        </div>
                        <div class="flex flex-col items-end">
                            <span class="font-bold text-xs text-gray-800">{{ line.amount | currency:
                                selectedInvoice.currency }}</span>
                            @if (line.quantity) {
                            <span class="text-xxs text-gray-400 mt-0.5">Qty: {{ line.quantity }}</span>
                            }
                        </div>
                    </div>
                    }
                </div>
            </div>

            <!-- Right panel: Detailed Firestore AI Token Usage logs -->
            <div class="lg:col-span-7 flex flex-col gap-4">
                <h4 class="font-bold text-sm text-gray-700 flex items-center gap-2">
                    <i class="fa fa-bar-chart text-primary-500"></i> AI Tokens Audit Records
                </h4>

                <div class="flex flex-col gap-3">
                    @if (loadingUsage && usageRecords.length === 0) {
                    <div class="flex justify-center items-center py-8">
                        <app-loading-container class="w-full"></app-loading-container>
                    </div>
                    } @else {
                    @for (record of usageRecords; track record.id) {
                    <div
                        class="p-3.5 rounded-xl border border-gray-100 bg-white shadow-sm flex flex-col gap-1.5 text-xs text-gray-600">
                        <div class="flex justify-between items-center">
                            <span class="font-bold text-gray-900 capitalize">{{ record.actionType?.replace('_', ' ')
                                }}</span>
                            <span class="text-xxs text-gray-400">{{ record.timestamp | date:'medium' }}</span>
                        </div>
                        <div
                            class="flex flex-row justify-between items-center pt-2 border-t border-gray-50 font-medium">
                            <div class="flex gap-4">
                                <span class="text-gray-500">Input: <strong class="text-gray-700">{{ record.inputTokens
                                        }}</strong></span>
                                <span class="text-gray-500">Output: <strong class="text-gray-700">{{ record.outputTokens
                                        }}</strong></span>
                            </div>
                            <span class="text-primary-600">Total: <strong>{{ record.totalTokens }}
                                    tokens</strong></span>
                        </div>
                    </div>
                    } @empty {
                    <p class="text-xs text-gray-400 text-center py-8 bg-gray-50 rounded-xl border border-dashed">
                        No AI token usage audit logs found for this billing period.
                    </p>
                    }
                    }

                    @if (hasMoreUsage && !loadingUsage) {
                    <button
                        class="bg-gray-50 hover:bg-gray-100 text-primary-600 font-bold py-2.5 px-4 rounded-xl border border-gray-200 transition text-xs text-center w-full mt-2"
                        (click)="loadUsageRecords(selectedInvoice, true)">
                        Load More Records
                    </button>
                    } @else if (loadingUsage && usageRecords.length > 0) {
                    <div class="flex justify-center py-4">
                        <i class="fa fa-spinner fa-spin text-primary-500 text-lg"></i>
                    </div>
                    }
                </div>
            </div>
        </div>
    </div>
    }


</div>